import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { MobileEventSelector } from "@/components/rider/MobileEventSelector";
import { DesktopEventSelector } from "@/components/rider/desktop/DesktopEventSelector";
import { LoadingEvents } from "@/components/rider/LoadingEvents";
import { CommunityEvent } from "@/types/rider";

// Breakaway Festival Events
const breakawayEvents: CommunityEvent[] = [
  {
    id: "breakaway-1",
    name: "Breakaway Festival",
    location: "The Palladium, 261 Main St, Worcester, MA 01608",
    region: "Boston",
    image: "/Breakaway_Promo_1.png",
    date: "Friday 9/5",
    startTime: "3 Hour Reservation - One Way | Capacity 15",
    price: 800
  },
  {
    id: "breakaway-2", 
    name: "Breakaway Festival",
    location: "The Palladium, 261 Main St, Worcester, MA 01608",
    region: "Boston",
    image: "/Breakaway_Promo_1.png",
    date: "Friday 9/5",
    startTime: "8 Hour Reservation - Round Trip | Capacity 15",
    price: 1500
  },
  {
    id: "breakaway-3",
    name: "Breakaway Festival", 
    location: "The Palladium, 261 Main St, Worcester, MA 01608",
    region: "Boston",
    image: "/Breakaway_Promo_2.png",
    date: "Saturday 9/6",
    startTime: "3 Hour Reservation - One Way | Capacity 15",
    price: 800
  },
  {
    id: "breakaway-4",
    name: "Breakaway Festival",
    location: "The Palladium, 261 Main St, Worcester, MA 01608", 
    region: "Boston",
    image: "/Breakaway_Promo_2.png",
    date: "Saturday 9/6",
    startTime: "8 Hour Reservation - Round Trip | Capacity 15",
    price: 1500
  }
];

const RiderEventSelectBreakaway = () => {
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const [isImmersive, setIsImmersive] = useState(false);

  useEffect(() => {
    // Simulate loading delay for consistency with main event select page
    const loadEvents = () => {
      setTimeout(() => {
        setEvents(breakawayEvents);
        setLoading(false);
      }, 800);
    };

    loadEvents();
  }, []);

  const handleEventSelection = (eventId: string, index: number) => {
    setSelectedEventId(eventId || null);
    setActiveIndex(index);
  };

  const handleResetSelection = () => {
    setSelectedEventId(null);
    setActiveIndex(0);
    setIsImmersive(false);
  };

  const handleContinue = (selectedAddOns?: any[], totalPrice?: number) => {
    if (selectedEventId) {
      const selectedEvent = events.find(event => event.id === selectedEventId);
      const isRoundTrip = selectedEvent?.startTime?.includes('Round Trip') ? 'true' : 'false';
      const venueAddress = "The Palladium, 261 Main St, Worcester, MA 01608";
      
      const params = new URLSearchParams({ 
        eventId: selectedEventId,
        capacity: '15',
        isRoundTrip: isRoundTrip,
        destination: venueAddress // Use 'destination' parameter that RiderQuote expects
      });
      
      // For round trips, handle return trip information
      if (isRoundTrip === 'true') {
        // Check if after-party add-on is selected for return destination
        const afterPartyAddon = selectedAddOns?.find(addon => 
          addon.description.includes('Ship Wrek') || addon.description.includes('DJ Diesel')
        );
        
        if (afterPartyAddon) {
          if (afterPartyAddon.description.includes('Ship Wrek')) {
            params.append('returnDestination', 'The Grand Boston, Seaport Boulevard, Boston, MA, USA');
          } else if (afterPartyAddon.description.includes('DJ Diesel')) {
            params.append('returnDestination', 'Big Night Live, Causeway Street, Boston, MA, USA');
          }
          // Return pickup is always from the venue for after-party
          params.append('returnPickup', venueAddress);
        } else {
          // Default return destination when no after-party add-on is selected
          // For round trips without add-ons, return to original pickup location
          params.append('returnDestination', ''); // Will be filled by user input
          params.append('returnPickup', venueAddress);
        }
      }
      
      if (totalPrice) {
        params.append('price', totalPrice.toString());
      }
      
      if (selectedAddOns && selectedAddOns.length > 0) {
        params.append('addOns', JSON.stringify(selectedAddOns));
        
        // Create notes from selected add-ons
        const addOnNotes = selectedAddOns
          .map(addon => `${addon.description} (+$${addon.price})`)
          .join('\n');
        params.append('notes', addOnNotes);
      }
      
      navigate(`/rider-quote?${params.toString()}`);
    }
  };

  const handleManualEntry = () => {
    navigate('/rider-quote');
  };

  const handlePrevCard = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
      setSelectedEventId(events[activeIndex - 1].id);
    }
  };

  const handleNextCard = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
      setSelectedEventId(events[activeIndex + 1].id);
    }
  };

  // Mobile-specific navigation handlers that only change activeIndex
  const handleMobilePrevCard = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
    }
  };

  const handleMobileNextCard = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
    }
  };

  const handleCardTap = (eventId: string, index: number) => {
    if (!isImmersive) {
      setIsImmersive(true);
    }
    handleEventSelection(eventId, index);
  };

  return (
    <RiderLayout>
      <div className="min-h-screen bg-white transition-colors duration-300">
        {loading ? (
          <LoadingEvents />
        ) : (
          <>
            <MobileEventSelector
              events={events}
              selectedEventId={selectedEventId}
              activeIndex={activeIndex}
              onEventSelect={handleEventSelection}
              onContinue={handleContinue}
              onManualEntry={handleManualEntry}
              onPrevCard={handleMobilePrevCard}
              onNextCard={handleMobileNextCard}
            />

            <DesktopEventSelector
              events={events}
              activeIndex={activeIndex}
              selectedEventId={selectedEventId}
              isImmersive={isImmersive}
              setIsImmersive={setIsImmersive}
              handleEventSelection={handleEventSelection}
              handlePrevCard={handlePrevCard}
              handleNextCard={handleNextCard}
              handleCardTap={handleCardTap}
              handleContinue={handleContinue}
              handleManualEntry={handleManualEntry}
              handleResetSelection={handleResetSelection}
            />
          </>
        )}
      </div>
    </RiderLayout>
  );
};

export default RiderEventSelectBreakaway;